import React, { useEffect, useState } from "react"
import { get, post } from "helpers/api_helper"
import { EMPLOYEE_SELF_PROFILE } from "helpers/url_helper"
import Notifications from "components/Notifications"

const APPLY_URL = "/api/employee/applyprofile"

const UserProfile = ({ auth, isAdmin }) => {
    const [profile, setProfile] = useState(null);
    const [pending, setPending] = useState(null);
    const [form, setForm] = useState({});
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [pendingRequests, setPendingRequests] = useState([]);

    useEffect(() => { fetchProfile(); }, []);

    const fetchProfile = async () => {
        setLoading(true);
        const res = await get(EMPLOYEE_SELF_PROFILE);
        if (res && (res.status === 200 || res?.status === 200 || res?.response)) {
            const p = res.response?.profile || res.response;
            setProfile(p);
            setPending(res.response?.pending_request || null);
            setForm({
                fullname: p?.fullname || `${p?.firstname || ''} ${p?.lastname || ''}`.trim() || auth?.fullname || "",
                mobile: p?.mobile || "",
                address: p?.address || "",
                emergency_contact: p?.emergency_contact || p?.emergency_mobile || "",
            });
        }
        setLoading(false);
    };

    // Admin: load all pending profile update requests
    const fetchAdminRequests = async () => {
        const res = await get("/api/approvals/list?document_type=profile_update&status=pending");
        if (res.status === 200) setPendingRequests(res.response?.rows || res.response || []);
    };

    useEffect(() => { if (isAdmin) fetchAdminRequests(); }, [isAdmin]);

    const submitUpdateRequest = async () => {
        if (!reason.trim()) { Notifications("Please enter a reason for the update.", "warning"); return; }
        setSaving(true);
        const res = await post(EMPLOYEE_SELF_PROFILE, { ...form, reason });
        if (res.status === 200) {
            Notifications("Update request sent! Awaiting admin approval.", "success");
            setReason("");
            fetchProfile();
        } else {
            Notifications(res.message || "Failed to submit request.", "error");
        }
        setSaving(false);
    };

    const handleAdminAction = async (requestId, action, rejReason = "") => {
        const res = await post(APPLY_URL, { request_id: requestId, action, rejection_reason: rejReason });
        if (res.status === 200) {
            Notifications(action === "approve" ? "Profile update approved and applied!" : "Request rejected.", action === "approve" ? "success" : "info");
            fetchAdminRequests();
            fetchProfile();
        } else {
            Notifications("Action failed.", "error");
        }
    };

    const inp = { width: "100%", padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", outline: "none", background: "#fff" };
    const lbl = { fontSize: "12px", fontWeight: "600", color: "#64748b", marginBottom: "5px", display: "block", textTransform: "uppercase", letterSpacing: "0.4px" };
    const box = { background: "#f8fafc", borderRadius: "10px", padding: "20px", marginBottom: "20px", border: "1px solid #e2e8f0" };
    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const initials = (name) => (name || "U").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

    if (loading) return (
        <div style={{ textAlign: "center", padding: "60px" }}>
            <i className="fas fa-spinner fa-spin text-primary" style={{ fontSize: "28px" }}></i>
            <p style={{ marginTop: "12px", color: "#94a3b8", fontSize: "13px" }}>Loading profile...</p>
        </div>
    );

    return (
        <div>
            {/* Profile Card Header */}
            <div style={{ background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)", borderRadius: "12px", padding: "24px", marginBottom: "24px", display: "flex", alignItems: "center", gap: "20px", color: "#fff" }}>
                <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "linear-gradient(135deg, #556ee6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", fontWeight: "800", color: "#fff", flexShrink: 0, border: "3px solid rgba(255,255,255,0.2)" }}>
                    {initials(profile?.fullname || auth?.fullname)}
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "20px", fontWeight: "800" }}>{profile?.fullname || auth?.fullname || "—"}</div>
                    <div style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>
                        {profile?.designation || profile?.dept || "Employee"} &nbsp;•&nbsp; ID: {profile?.emp_id || auth?.empid || "—"}
                    </div>
                </div>
                <div>
                    {pending ? (
                        <span style={{ background: "#fbbf24", color: "#78350f", padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
                            <i className="fas fa-clock"></i> Update Pending Approval
                        </span>
                    ) : (
                        <span style={{ background: "#22c55e", color: "#fff", padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
                            <i className="fas fa-check-circle"></i> Profile Active
                        </span>
                    )}
                </div>
            </div>

            {/* Pending request banner */}
            {pending && (
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "10px", padding: "14px 18px", marginBottom: "20px", display: "flex", alignItems: "flex-start", gap: "12px" }}>
                    <i className="fas fa-hourglass-half" style={{ color: "#d97706", fontSize: "18px", flexShrink: 0, marginTop: "2px" }}></i>
                    <div>
                        <div style={{ fontWeight: "700", color: "#92400e", fontSize: "13px" }}>Profile Update Pending Admin Approval</div>
                        <div style={{ fontSize: "12px", color: "#b45309", marginTop: "3px" }}>
                            Submitted on {new Date(pending.requested_at).toLocaleString("en-IN")} &nbsp;•&nbsp; Reason: <em>{pending.reason}</em>
                        </div>
                        <div style={{ fontSize: "12px", color: "#78350f", marginTop: "3px" }}>Your changes will be applied once an admin approves the request.</div>
                    </div>
                </div>
            )}

            {/* Editable fields */}
            <div style={box}>
                <div style={{ fontWeight: "700", fontSize: "13px", color: "#334155", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="bx bx-user text-primary"></i> Personal Information
                    <span style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "400" }}>Changes require admin approval</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                    <div>
                        <label style={lbl}>Full Name</label>
                        <input style={inp} value={form.fullname} onChange={e => set("fullname", e.target.value)} placeholder="Your full name" />
                    </div>
                    <div>
                        <label style={lbl}>Mobile Number</label>
                        <input style={inp} type="tel" value={form.mobile} onChange={e => set("mobile", e.target.value)} placeholder="Mobile number" />
                    </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                    <div>
                        <label style={lbl}>Emergency Contact</label>
                        <input style={inp} type="tel" value={form.emergency_contact} onChange={e => set("emergency_contact", e.target.value)} placeholder="Emergency contact number" />
                    </div>
                    <div>
                        <label style={lbl}>Address</label>
                        <input style={inp} value={form.address} onChange={e => set("address", e.target.value)} placeholder="Residential address" />
                    </div>
                </div>
            </div>

            {/* Read-only fields */}
            <div style={box}>
                <div style={{ fontWeight: "700", fontSize: "13px", color: "#334155", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="bx bx-lock-alt text-muted"></i> <span style={{ color: "#64748b" }}>Read-Only Information</span>
                    <span style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "400" }}>Managed by HR / Admin</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
                    {[
                        ["Department", profile?.dept || "—"],
                        ["Designation", profile?.designation || "—"],
                        ["Employee Code", profile?.emp_id || "—"],
                        ["Date of Joining", profile?.doj || "—"],
                        ["Email / Username", profile?.username || auth?.username || "—"],
                        ["Branch", profile?.branch_name || "—"],
                    ].map(([l, v]) => (
                        <div key={l} style={{ background: "#f1f5f9", borderRadius: "8px", padding: "12px 14px" }}>
                            <div style={{ ...lbl, marginBottom: "4px" }}>{l}</div>
                            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1e293b" }}>{v}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Reason + Submit */}
            <div style={{ ...box, marginBottom: "24px" }}>
                <div style={{ fontWeight: "700", fontSize: "13px", color: "#334155", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="bx bx-edit text-primary"></i> Reason for Update
                </div>
                <textarea style={{ ...inp, height: "80px", resize: "vertical" }} value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="Briefly explain why you need to update your profile (required)..." />
                <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "6px" }}>
                    <i className="fas fa-info-circle me-1"></i> Your request will be reviewed by an admin before changes are applied.
                </div>
            </div>

            <button onClick={submitUpdateRequest} disabled={saving || !!pending} style={{ padding: "10px 28px", background: pending ? "#94a3b8" : "#2563eb", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "700", fontSize: "13px", cursor: pending ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> Submitting...</> : pending ? <><i className="fas fa-hourglass-half" /> Request Pending...</> : <><i className="bx bx-send" /> Submit Update Request</>}
            </button>

            {/* ADMIN SECTION: Pending profile update approvals */}
            {isAdmin && (
                <div style={{ marginTop: "36px" }}>
                    <div style={{ fontWeight: "800", fontSize: "15px", color: "#0f172a", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
                        <i className="fas fa-user-shield text-primary"></i> Profile Update Requests
                        <span style={{ background: "#fee2e2", color: "#dc2626", padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "700" }}>{pendingRequests.length} Pending</span>
                    </div>

                    {pendingRequests.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "32px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0", color: "#94a3b8", fontSize: "13px" }}>
                            <i className="fas fa-check-circle" style={{ fontSize: "28px", color: "#22c55e", display: "block", marginBottom: "10px" }}></i>
                            No pending profile update requests.
                        </div>
                    ) : pendingRequests.map(req => (
                        <ApprovalRequestCard key={req.id} req={req} onAction={handleAdminAction} />
                    ))}
                </div>
            )}
        </div>
    );
};

const ApprovalRequestCard = ({ req, onAction }) => {
    const [rejecting, setRejecting] = useState(false);
    const [rejReason, setRejReason] = useState("");
    const newVals = (() => { try { return JSON.parse(req.new_values); } catch { return {}; } })();

    return (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "18px", marginBottom: "14px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                <div>
                    <div style={{ fontWeight: "700", fontSize: "14px", color: "#1e293b" }}>{req.requested_by_name}</div>
                    <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>
                        ID: {req.requested_by} &nbsp;•&nbsp; {new Date(req.requested_at).toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px", fontStyle: "italic" }}>Reason: {req.reason}</div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => onAction(req.id, "approve")}
                        style={{ padding: "7px 16px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "6px", fontWeight: "700", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}>
                        <i className="fas fa-check" /> Approve
                    </button>
                    <button onClick={() => setRejecting(r => !r)}
                        style={{ padding: "7px 16px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: "6px", fontWeight: "700", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}>
                        <i className="fas fa-times" /> Reject
                    </button>
                </div>
            </div>

            <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "12px", marginBottom: rejecting ? "12px" : 0 }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Proposed Changes</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px" }}>
                    {Object.entries(newVals).filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "capitalize", minWidth: "120px" }}>{k.replace(/_/g, " ")}</span>
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>{v}</span>
                        </div>
                    ))}
                </div>
            </div>

            {rejecting && (
                <div style={{ marginTop: "10px" }}>
                    <textarea style={{ width: "100%", padding: "9px 12px", border: "1px solid #fca5a5", borderRadius: "8px", fontSize: "12px", outline: "none", height: "60px", resize: "none" }}
                        placeholder="Reason for rejection..." value={rejReason} onChange={e => setRejReason(e.target.value)} />
                    <button onClick={() => { onAction(req.id, "reject", rejReason); setRejecting(false); }}
                        style={{ marginTop: "6px", padding: "7px 16px", background: "#dc2626", color: "#fff", border: "none", borderRadius: "6px", fontWeight: "700", fontSize: "12px", cursor: "pointer" }}>
                        Confirm Rejection
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserProfile;
